import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { TITLE_MAX_LENGTH } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import { getGroupName } from "app/pages/keyword-analysis/utils/getGroupName";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { useKeywordListComposerContext } from "./KeywordListComposerContext";
import type { Mode } from "./types";

type Status =
    | {
          isValid: true;
          errorMessage?: never;
      }
    | {
          isValid: false;
          errorMessage: "duplicate_name" | "empty_name" | "name_too_long";
      };

function getStatus(
    keywordGroups: IUserKeywordGroup[],
    groupName: string,
    mode: Mode,
    keywordGroupToEdit?: IUserKeywordGroup,
): Status {
    const keywordGroup = keywordGroups.find((group) => group.Name === groupName);
    const createPredicate = keywordGroup?.Name === groupName;
    const differentIdPredicate = keywordGroupToEdit?.Id !== keywordGroup?.Id;
    const editPredicate = differentIdPredicate && createPredicate;
    const isNameExists = mode === "edit_existing_list" ? editPredicate : createPredicate;

    if (isNameExists) {
        return { isValid: false, errorMessage: "duplicate_name" };
    }

    const nameLength = groupName.length;

    if (nameLength === 0) {
        return { isValid: false, errorMessage: "empty_name" };
    }

    if (nameLength >= TITLE_MAX_LENGTH) {
        return { isValid: false, errorMessage: "name_too_long" };
    }

    return { isValid: true };
}

async function getInitialKeywordGroupName(
    seedKeyword?: string,
    keywordGroupToEdit?: IUserKeywordGroup,
): Promise<string> {
    if (seedKeyword) {
        return await getGroupName(seedKeyword);
    }

    if (keywordGroupToEdit) {
        return keywordGroupToEdit.Name;
    }

    return "";
}

interface KeywordGroupNameContextShape {
    keywordGroupName: string;
    keywordGroupNameValidationStatus: Status;
    changeKeywordGroupName: (keywordGroupName: string) => void;
}

const KeywordGroupNameContext = createContext<KeywordGroupNameContextShape | null>(null);

interface KeywordGroupNameProviderProps {
    userGroups?: IUserKeywordGroup[];
    children: ReactNode | ReactNode[];
}

export const KeywordGroupNameProvider = ({ userGroups = [], children }: KeywordGroupNameProviderProps) => {
    const keywordListComposerContext = useKeywordListComposerContext();

    if (!keywordListComposerContext) {
        throw new Error("You must wrap KeywordGroupNameProvider in KeywordListComposerProvider");
    }

    const { seedKeyword, keywordGroupToEdit, mode, markShouldConfirmBeforeClose } = keywordListComposerContext;
    const [keywordGroupName, setKeywordGroupName] = useState("");

    useEffect(() => {
        const initializeKeywordGroupName = async () => {
            const initialName = await getInitialKeywordGroupName(seedKeyword, keywordGroupToEdit);
            setKeywordGroupName(initialName);
        };

        void initializeKeywordGroupName();
    }, [seedKeyword, keywordGroupToEdit]);
    const [keywordGroupNameValidationStatus, setKeywordGroupNameValidationStatus] = useState<Status>({ isValid: true });
    const [isPristine, setIsPristine] = useState(true);

    const changeKeywordGroupName = useCallback(
        (newKeywordGroupName: string) => {
            if (isPristine) {
                setIsPristine(false);
                markShouldConfirmBeforeClose();
            }
            setKeywordGroupName(newKeywordGroupName);
            setKeywordGroupNameValidationStatus(getStatus(userGroups, newKeywordGroupName, mode, keywordGroupToEdit));
        },
        [isPristine, userGroups, mode, keywordGroupToEdit, markShouldConfirmBeforeClose],
    );

    const value = useMemo(
        () => ({ keywordGroupName, keywordGroupNameValidationStatus, changeKeywordGroupName }),
        [keywordGroupName, keywordGroupNameValidationStatus, changeKeywordGroupName],
    );

    // re-validate the keyword group name if user keyword groups change (e.g. when refetching keyword groups)
    useEffect(() => {
        if (!isPristine) {
            setKeywordGroupNameValidationStatus(getStatus(userGroups, keywordGroupName, mode, keywordGroupToEdit));
        }
    }, [userGroups, keywordGroupName, mode, keywordGroupToEdit, isPristine]);

    return <KeywordGroupNameContext.Provider value={value}>{children}</KeywordGroupNameContext.Provider>;
};

export const useKeywordGroupNameContext = () => {
    const t = useTranslation();
    const keywordGroupNameContext = useContext(KeywordGroupNameContext);

    if (!keywordGroupNameContext) {
        throw new Error("useKeywordGroupNameContext must be used within a KeywordGroupNameProvider");
    }

    const translateValidationStatus = useCallback(
        (validationStatus: Status): { isValid: boolean; errorMessage: string | undefined } => {
            switch (validationStatus.errorMessage) {
                case "duplicate_name":
                    return { isValid: false, errorMessage: t("keywordanalysis.groupwizard.duplicatename") };
                case "empty_name":
                    return { isValid: false, errorMessage: t("auto.keyword.list.generator.header.group.name.empty") };
                case "name_too_long": {
                    return {
                        isValid: false,
                        errorMessage: t("auto.keyword.list.generator.header.group.name.too.long", {
                            maxLength: TITLE_MAX_LENGTH,
                        }),
                    };
                }
                default:
                    return { isValid: true, errorMessage: undefined };
            }
        },
        [t],
    );

    const content = useMemo(
        () => ({
            keywordGroupName: keywordGroupNameContext.keywordGroupName,
            keywordGroupNameValidationStatus: translateValidationStatus(
                keywordGroupNameContext.keywordGroupNameValidationStatus,
            ),
            changeKeywordGroupName: keywordGroupNameContext.changeKeywordGroupName,
        }),
        [keywordGroupNameContext, translateValidationStatus],
    );

    return content;
};
